import { styled } from "@linaria/react"
import { format } from "date-fns"
import { observer } from "mobx-react-lite"
import { useState } from "react"

import { Badge } from "@/components/Badge/Badge.tsx"
import { Button } from "@/components/Button/Button.tsx"
import { Ellipsis } from "@/components/Ellipsis/Ellipsis.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Heading2, Heading6 } from "@/components/Heading/Heading.tsx"
import { RepositoryCredUpdate } from "@/components/ProjectSettingsDialog/RepositoryCredUpdate.tsx"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { CheckmarkMedium, ErrorFill, GithubFill, Icon12, Icon20, SuccessFill } from "@/icons/Icons.ts"
import { GitHubCommonUtil } from "@/lib/GitHubCommonUtil.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { ProjectMirroringModel } from "@/components/ProjectSettingsDialog/ProjectMirroringModel.ts"


export const ProjectMirroringTab = observer((props: {
  model: ProjectMirroringModel
}) => {
  const model = props.model
  const integrationModel = model.integrationModel

  const repositoryFetchStatus = model.status.get()
  const lastSync = repositoryFetchStatus?.lastSync
  const remoteUrl = repositoryFetchStatus?.remoteUrl
  const getTokenUrl = repositoryFetchStatus?.getTokenUrl
  const status = repositoryFetchStatus?.status

  const [updatingToken, setUpdatingToken] = useState(false)

  const unauthorizedFetchError = model.status.get()?.authError === true

  return (
    <Vertical gap="16px">
      <Heading2>Git Repository</Heading2>

      <Heading6>Mirroring</Heading6>

      {remoteUrl && (
        <MirrorBg height="40px">
          {GitHubCommonUtil.isGitHubRepository(remoteUrl) && <GithubFill />}

          <Tooltip content={remoteUrl}>
            <StyledEllipsis>{remoteUrl}</StyledEllipsis>
          </Tooltip>

          {lastSync && (!status || status === "Success") && (
            <SuccessSyncBadge kind="success" variant="small">
              <CheckmarkMedium className={Icon12} />
              Synced at {formatTime(lastSync)}
            </SuccessSyncBadge>
          )}
          {lastSync && status && status === "Running" && (
            <SyncBadge kind="disabled" variant="small">
              Syncing
            </SyncBadge>
          )}
          {lastSync && status && (status === "Paused" || status === "Error") && (
            <SyncBadge kind="error" variant="small">
              Synced at {formatTime(lastSync)}
            </SyncBadge>
          )}
        </MirrorBg>
      )}

      {status === "Running" && <Spinner text={ellipsize("Sync in progress")} size={20} />}

      {status === "Success" && (
        <Horizontal alignItems="center" gap="4px">
          <SuccessFill className={Icon20} color="var(--success-color)" />Synced successfully
        </Horizontal>
      )}

      {remoteUrl && !unauthorizedFetchError && updatingToken && (
        <RepositoryCredUpdate
          hasAuthError={unauthorizedFetchError}
          authOptions={model.authOptions.get()}
          getTokenUrl={getTokenUrl ?? null}
          saveToken={token => {
            void model.setRepositoryToken(remoteUrl, token)
            setUpdatingToken(false)
          }}
          saveSsh={(key: string, password: string | null) => {
            void model.setRepositorySshKey(remoteUrl, key, password)
            setUpdatingToken(false)
          }}
          model={model}
          onClose={() => setUpdatingToken(false)}
        />
      )}

      {
        (remoteUrl && unauthorizedFetchError) ? (
          <RepositoryCredUpdate
            hasAuthError={unauthorizedFetchError}
            authOptions={model.authOptions.get()}
            getTokenUrl={getTokenUrl ?? null}
            saveToken={token => model.setRepositoryToken(remoteUrl, token)}
            saveSsh={(key: string, password: string | null) => model.setRepositorySshKey(remoteUrl, key, password)}
            model={model}
            onClose={() => setUpdatingToken(false)}
          />
        ) : (
          <>
            {status === "Error" && (
              <Horizontal alignItems="center" gap="4px">
                <ErrorFill className={Icon20} color="var(--error-color)" />Sync finished with an error
              </Horizontal>
            )}

            {status === "Paused" && (
              <Horizontal alignItems="center" gap="4px">
                <ErrorFill className={Icon20} color="var(--secondary-text-color)" />Sync is paused
              </Horizontal>
            )}
          </>
        )
      }

      <Horizontal gap="8px">
        <Button kind="alternative" size="large" onClick={() => model.startFetch()} disabled={status === "Running"}>
          Sync remote repository
        </Button>

        {remoteUrl && !unauthorizedFetchError && !updatingToken && (
          <Button
            size="large"
            kind="alternative"
            onClick={() => setUpdatingToken(true)}
            disabled={!model.canEditProject.get()}
          >
            Update credentials
          </Button>
        )}

      </Horizontal>

      {integrationModel.gitHubStatus.get()?.isGitHubRepo && (
        <>
          <Heading6>GitHub Integration</Heading6>

          {integrationModel.gitHubStatus.get()?.isConnected && (
            <Horizontal alignItems="center" gap="8px">
              <ConnectionBg>
                <GreenDot /> Connected
              </ConnectionBg>

              <Button
                size="large"
                kind="alternative"
                onClick={() => integrationModel.configureGitHub()}
                disabled={!model.canEditProject.get()}
              >
                Configure
              </Button>
            </Horizontal>
          )}

          {!integrationModel.gitHubStatus.get()?.isConnected && (
            <>
              <span>Authorize Matter to collaborate on this repository</span>

              <Horizontal alignItems="center" gap="8px">
                <ButtonWithIcon kind="alternative" size="large" onClick={() => integrationModel.connectToGitHub()}>
                  <Horizontal alignItems="center" gap="8px">
                    <GithubFill className={Icon20} />
                    Install GitHub App
                  </Horizontal>
                </ButtonWithIcon>
              </Horizontal>
            </>
          )}
        </>
      )}
    </Vertical>
  )
})

function formatTime(timestamp: number) {
  const date = new Date(timestamp)
  return format(date, "HH:mm")
}

const GreenDot = styled.div`
  width: 8px;
  height: 8px;
  background-color: var(--success-color);
  border-radius: var(--border-radius-round);
  flex-shrink: 0;
`

const MirrorBg = styled(Horizontal)`
  flex-grow: 1;
  align-items: center;
  gap: 8px;

  height: 40px;
  padding: 0 16px;

  background: var(--text-color-2);
  border-radius: var(--border-radius-12);
`

const SyncBadge = styled(Badge)`
  text-transform: uppercase;
`

const SuccessSyncBadge = styled(SyncBadge)`
  background: radial-gradient(142.53% 100% at 100% 50%, #1CFFBB33 0%, #0CEE6E00 100%), #E7FAE5;
`

const ConnectionBg = styled(Horizontal)`
  flex-grow: 1;
  align-items: center;
  gap: 8px;

  height: 40px;
  padding: 0 16px;

  background: radial-gradient(142.53% 100% at 100% 50%, #1CFFBB33 0%, #0CEE6E00 100%), linear-gradient(0deg, #E7FAE5 0%, #E7FAE5 100%), rgb(58 58 59 / 5%);
  border-radius: var(--border-radius-12);
`

const StyledEllipsis = styled(Ellipsis)`
  flex-grow: 1;
`

const ButtonWithIcon = styled(Button)`
  gap: 8px;
`
